// src/App.js
import React, { useState } from 'react';
import { Editor, Frame, Element, useEditor } from "@craftjs/core";
import {
    Typography,
    Paper,
    IconButton,
    Box,
    Tooltip,
    Button,
    useTheme,
    Tab,
    Tabs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Snackbar,
    Fade,
    AppBar,
    Toolbar,
} from '@material-ui/core';
import {
    Save as SaveIcon,
    CloudUpload as UploadIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
    Visibility as PreviewIcon,
    VisibilityOff as HidePreviewIcon,
    GridOn as GridIcon,
    Settings as SettingsIcon,
    Add as AddIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Close as CloseIcon
} from '@material-ui/icons';

import { Container } from './components/user/Container';
import { Text } from './components/user/Text';
import { Button as UserButton } from './components/user/Button';
import { Image } from './components/user/Image';
import { Divider } from './components/user/Divider';
import { Section } from "./components/user/Section";
import { Toolbox } from './components/Toolbox';
import { SettingsPanel } from './components/SettingsPanel';
import { Flexbox } from "./components/user/Flexbox";
import { Heading } from './components/user/Heading';
import { Paragraph } from './components/user/Paragraph';

const RenderNode = ({ render }) => {
    return (
        <Box sx={{ position: 'relative' }}>
            {render}
        </Box>
    );
};

export default function App() {
    const theme = useTheme();
    const [previewMode, setPreviewMode] = useState(false);
    const [fullscreenMode, setFullscreenMode] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [showGrid, setShowGrid] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [jsonContent, setJsonContent] = useState('');
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);


    return (
        <Box sx={{
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: '#f5f7fa'
        }}>
            <Editor
                resolver={{
                    Container,
                    Text,
                    UserButton,
                    Image,
                    Divider,
                    Section,
                    Flexbox,
                    Heading,
                    Paragraph,
                    Box
                }}
                onRender={RenderNode}
                enabled={!previewMode}
                onError={error => console.error('Craft.js error:', error)}
            >
                <EditorContent
                    previewMode={previewMode}
                    setPreviewMode={setPreviewMode}
                    fullscreenMode={fullscreenMode}
                    setFullscreenMode={setFullscreenMode}
                    showGrid={showGrid}
                    setShowGrid={setShowGrid}
                    dialogOpen={dialogOpen}
                    setDialogOpen={setDialogOpen}
                    jsonContent={jsonContent}
                    setJsonContent={setJsonContent}
                    snackbarMessage={snackbarMessage}
                    setSnackbarMessage={setSnackbarMessage}
                    snackbarOpen={snackbarOpen}
                    setSnackbarOpen={setSnackbarOpen}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    theme={theme}
                />
            </Editor>
        </Box>
    );
}

const EditorContent = ({
                           previewMode,
                           setPreviewMode,
                           fullscreenMode,
                           setFullscreenMode,
                           showGrid,
                           setShowGrid,
                           dialogOpen,
                           setDialogOpen,
                           jsonContent,
                           setJsonContent,
                           snackbarMessage,
                           setSnackbarMessage,
                           snackbarOpen,
                           setSnackbarOpen,
                           activeTab,
                           setActiveTab,
                           theme
                       }) => {
    const { actions, query } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleSave = () => {
        const json = query.serialize();
        const formattedJson = JSON.stringify(JSON.parse(json), null, 2);

        const blob = new Blob([formattedJson], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'page-design.json';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSnackbarMessage('Design saved successfully');
        setSnackbarOpen(true);
    };

    const handleImport = () => {
        try {
            const parsed = JSON.parse(jsonContent);
            actions.deserialize(JSON.stringify(parsed));
            setDialogOpen(false);
            setJsonContent('');
            setSnackbarMessage('Design imported successfully');
            setSnackbarOpen(true);
        } catch (error) {
            setSnackbarMessage('Import failed: Invalid JSON format');
            setSnackbarOpen(true);
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setJsonContent(e.target.result);
            };
            reader.readAsText(file);
        }
    };

    // FullscreenPreview Component
    // FullscreenPreview Component
    const FullscreenPreview = () => {
        const { query } = useEditor();
        const [initialContent, setInitialContent] = React.useState(null);

        React.useEffect(() => {
            // 获取当前编辑器状态的序列化数据
            const json = query.serialize();
            setInitialContent(json);
        }, [query]);

        return (
            <Fade in={fullscreenMode}>
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'white',
                    zIndex: theme.zIndex.modal + 1,
                    overflow: 'auto'
                }}>
                    <Box>
                        <Editor
                            resolver={{
                                Container,
                                Text,
                                UserButton,
                                Image,
                                Divider,
                                Section,
                                Flexbox,
                                Heading,
                                Paragraph,
                                Box
                            }}
                            enabled={false}
                            onRender={RenderNode}
                        >
                            {initialContent && (
                                <Frame data={initialContent}>
                                    <Element
                                        canvas
                                        is={Container}
                                        padding={20}
                                        background="#ffffff"
                                    />
                                </Frame>
                            )}
                        </Editor>
                    </Box>
                </Box>
            </Fade>
        );
    };


    return (
        <>
            {fullscreenMode ? (
                <FullscreenPreview />
            ) : (
                <>
                    {/* Main Content Area */}
                    <Box sx={{
                        flexGrow: 1,
                        p: 3,
                        marginRight: previewMode ? 0 : '400px',
                        transition: 'all 0.3s'
                    }}>
                        <Box sx={{
                            maxWidth: '1200px',
                            margin: '0 auto',
                            position: 'relative'
                        }}>
                            {/* Canvas Controls */}
                            <Paper sx={{
                                mb: 3,
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                            }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setShowGrid(!showGrid)}
                                    startIcon={<GridIcon />}
                                    sx={{
                                        borderRadius: '6px'
                                    }}
                                >
                                    {showGrid ? 'Hide Grid' : 'Show Grid'}
                                </Button>
                                <Box sx={{ flexGrow: 1 }} />
                                <Tooltip title="Undo">
                                    <IconButton
                                        size="small"
                                        onClick={() => actions.history.undo()}
                                        sx={{ backgroundColor: '#f5f5f5' }}
                                    >
                                        <UndoIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Redo">
                                    <IconButton
                                        size="small"
                                        onClick={() => actions.history.redo()}
                                        sx={{ backgroundColor: '#f5f5f5' }}
                                    >
                                        <RedoIcon />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    sx={{
                                        borderRadius: '6px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<UploadIcon />}
                                    onClick={() => setDialogOpen(true)}
                                    sx={{
                                        borderRadius: '6px'
                                    }}
                                >
                                    Import
                                </Button>
                                <Tooltip title="Toggle Fullscreen Preview">
                                    <IconButton
                                        color="primary"
                                        onClick={() => setFullscreenMode(!fullscreenMode)}
                                        sx={{ backgroundColor: '#f5f5f5' }}
                                    >
                                        {fullscreenMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Paper>

                            {/* Main Canvas */}
                            <Paper sx={{
                                backgroundColor: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                position: 'relative',
                                minHeight: '800px'
                            }}>
                                <Frame>
                                    <Element
                                        is={Container}
                                        padding={20}
                                        background="#ffffff"
                                        canvas
                                        data-cy="root-container"
                                    />
                                </Frame>

                                {/* Grid Overlay */}
                                {showGrid && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `repeating-linear-gradient(
                                    0deg,
                                    transparent,
                                    transparent 19px,
                                    rgba(0, 0, 0, 0.03) 20px
                                ),
                                repeating-linear-gradient(
                                    90deg,
                                    transparent,
                                    transparent 19px,
                                    rgba(0, 0, 0, 0.03) 20px
                                )`,
                                        pointerEvents: 'none',
                                        zIndex: 1,
                                        borderRadius: '8px'
                                    }} />
                                )}
                            </Paper>
                        </Box>
                    </Box>

                    {/* Right Panel */}
                    <Box sx={{
                        position: 'fixed',
                        right: previewMode ? '-400px' : 0,
                        top: 0,
                        width: '400px',
                        height: '100vh',
                        backgroundColor: 'white',
                        borderLeft: '1px solid rgba(0,0,0,0.08)',
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: theme.zIndex.drawer + 1,
                        transition: 'right 0.3s ease',
                        boxShadow: '-2px 0 8px rgba(0,0,0,0.05)'
                    }}>
                        {/* Top Actions */}
                        <Box sx={{
                            p: 2,
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: '#f8f9fa'
                        }}>
                            <Typography variant="h6" sx={{
                                flexGrow: 1,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                color: '#2c3e50'
                            }}>
                                Page Editor
                            </Typography>
                            <Tooltip title="Undo">
                                <IconButton
                                    size="small"
                                    onClick={() => actions.history.undo()}
                                    sx={{ backgroundColor: '#fff' }}
                                >
                                    <UndoIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Redo">
                                <IconButton
                                    size="small"
                                    onClick={() => actions.history.redo()}
                                    sx={{ backgroundColor: '#fff' }}
                                >
                                    <RedoIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={previewMode ? "Exit Preview" : "Preview"}>
                                <IconButton
                                    size="small"
                                    onClick={() => setPreviewMode(!previewMode)}
                                    sx={{ backgroundColor: '#fff' }}
                                >
                                    {previewMode ? <HidePreviewIcon /> : <PreviewIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{
                                borderBottom: 1,
                                borderColor: 'divider',
                                backgroundColor: '#fff'
                            }}
                        >
                            <Tab
                                icon={<AddIcon />}
                                label="Components"
                                sx={{
                                    minHeight: '60px',
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main
                                    }
                                }}
                            />
                        </Tabs>

                        {/* Tab Panels */}
                        <Box sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            backgroundColor: '#f8f9fa'
                        }}>
                            {activeTab === 0 ? (
                                <Toolbox />
                            ) : (
                                <SettingsPanel />
                            )}
                        </Box>
                    </Box>

                    {/* Import Dialog */}
                    <Dialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        fullWidth
                        maxWidth="md"
                        sx={{
                            '& .MuiDialog-paper': {
                                borderRadius: '12px'
                            }
                        }}
                    >
                        <DialogTitle sx={{
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            backgroundColor: '#f8f9fa'
                        }}>
                            Import Design
                        </DialogTitle>
                        <DialogContent sx={{ p: 3 }}>
                            <Box mb={3}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Upload JSON File
                                </Typography>
                                <input
                                    type="file"
                                    accept=".json"
                                    onChange={handleFileUpload}
                                    style={{
                                        marginBottom: '1rem',
                                        width: '100%',
                                        padding: '1rem',
                                        border: '2px dashed rgba(0,0,0,0.12)',
                                        borderRadius: '8px'
                                    }}
                                />
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Or Paste JSON Content
                            </Typography>
                            <TextField
                                multiline
                                fullWidth
                                rows={10}
                                placeholder="Paste JSON content here..."
                                value={jsonContent}
                                onChange={(e) => setJsonContent(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px'
                                    }
                                }}
                            />
                        </DialogContent>
                        <DialogActions sx={{
                            p: 2.5,
                            borderTop: '1px solid rgba(0,0,0,0.08)'
                        }}>
                            <Button
                                onClick={() => setDialogOpen(false)}
                                sx={{
                                    borderRadius: '6px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleImport}
                                color="primary"
                                variant="contained"
                                disabled={!jsonContent}
                                sx={{
                                    borderRadius: '6px',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Import
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Snackbar for notifications */}
                    <Snackbar
                        open={snackbarOpen}
                        autoHideDuration={3000}
                        onClose={() => setSnackbarOpen(false)}
                        message={snackbarMessage}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        sx={{
                            '& .MuiSnackbarContent-root': {
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }
                        }}
                    />
                </>
            )}
        </>
    );
};